// PRO Drift Auditor -- historical AI Regime PRO rebalance trade ledger.
//
// Every rebalance the AI engine executed over the backtest: date, detected
// regime, trigger ticker, max relative drift, turnover, fee/tax cost and the
// portfolio wealth after the trade. Scrollable for long histories.

import React, { useMemo, useState } from 'react';
import { History } from 'lucide-react';

import { Card } from './Card';
import { SymbolLink } from './SymbolLink';
import { fmtMoney, fmtPct } from '../format';
import { REGIME_META, type RebalanceTradeEvent } from '../types';

const PAGE = 12;

export const TradeLedgerTable: React.FC<{
    rows: RebalanceTradeEvent[];
    routes?: Record<string, string>;
}> = ({ rows, routes }) => {
    const [showAll, setShowAll] = useState(false);
    const visible = useMemo(() => (showAll ? rows : rows.slice(0, PAGE)), [rows, showAll]);

    if (rows.length === 0) {
        return (
            <Card
                title="Forge Ultra Rebalance Trade Ledger"
                subtitle="Every historical rebalance the Forge Ultra Regime PRO engine executed."
                icon={<History className="h-4 w-4" />}
            >
                <p className="py-8 text-center text-sm text-slate-400">
                    The Forge Ultra engine held through the full window without a corridor-triggered rebalance.
                </p>
            </Card>
        );
    }

    return (
        <Card
            title="Forge Ultra Rebalance Trade Ledger"
            subtitle={`${rows.length} regime-aware rebalance event${rows.length === 1 ? '' : 's'} over the backtest.`}
            icon={<History className="h-4 w-4" />}
            right={
                rows.length > PAGE && (
                    <button
                        type="button"
                        onClick={() => setShowAll((v) => !v)}
                        className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700"
                    >
                        {showAll ? 'Show less' : `Show all ${rows.length}`}
                    </button>
                )
            }
            bodyClassName=""
        >
            <div className="max-h-[28rem] overflow-auto">
                <table className="w-full text-sm border-collapse">
                    <thead className="sticky top-0 z-10">
                        <tr className="bg-slate-50 dark:bg-slate-800 border-y border-slate-100 dark:border-slate-700 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                            <th className="px-3.5 py-2.5 text-left">#</th>
                            <th className="px-3.5 py-2.5 text-left">Date</th>
                            <th className="px-3.5 py-2.5 text-left">Regime</th>
                            <th className="px-3.5 py-2.5 text-left">Trigger</th>
                            <th className="px-3.5 py-2.5 text-right">Max Drift</th>
                            <th className="px-3.5 py-2.5 text-right">Turnover</th>
                            <th className="px-3.5 py-2.5 text-right">Fee+Tax</th>
                            <th className="px-3.5 py-2.5 text-right">Wealth After</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visible.map((ev) => {
                            const regime = REGIME_META[ev.regime_state];
                            return (
                                <tr
                                    key={ev.event_index}
                                    className="border-b border-slate-50 dark:border-slate-800/60 last:border-0 hover:bg-slate-50/60 dark:hover:bg-slate-800/40"
                                >
                                    <td className="px-3.5 py-2.5 tabular-nums text-slate-400">{ev.event_index}</td>
                                    <td className="px-3.5 py-2.5 tabular-nums text-slate-700 dark:text-slate-200 whitespace-nowrap">{ev.date}</td>
                                    <td className="px-3.5 py-2.5">
                                        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-bold ${regime.bg} ${regime.text}`}>
                                            <span className="h-1.5 w-1.5 rounded-full" style={{ background: regime.color }} />
                                            {regime.label}
                                        </span>
                                    </td>
                                    <td className="px-3.5 py-2.5 whitespace-nowrap">
                                        <SymbolLink symbol={ev.trigger_ticker} routes={routes} />
                                    </td>
                                    <td className="px-3.5 py-2.5 text-right tabular-nums text-amber-600 dark:text-amber-400">{fmtPct(ev.max_relative_drift_pct, 1)}</td>
                                    <td className="px-3.5 py-2.5 text-right tabular-nums text-slate-600 dark:text-slate-300">{fmtPct(ev.turnover_pct, 1)}</td>
                                    <td className="px-3.5 py-2.5 text-right tabular-nums text-rose-600 dark:text-rose-400">{fmtMoney(ev.fee_and_tax_cost_dollars)}</td>
                                    <td className="px-3.5 py-2.5 text-right tabular-nums font-semibold text-slate-900 dark:text-white">{fmtMoney(ev.portfolio_wealth_after_trade)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </Card>
    );
};
